'use client'

import Image from 'next/image'
import { getPlaySettings } from '@/lib/phishtrainer/play-settings'
import { resolveBackendFileUrl } from '@/lib/api/config'
import {
  resolvePlayerLogos,
  PLAYER_LOGO_MAX_HEIGHT_CLASS,
  type PlayerLogos,
} from '@/lib/phishtrainer/player-logo'
import { useEffect, useState } from 'react'
import { useTranslation } from '@/lib/i18n'

/**
 * Player-portal brand logo with proper resolution:
 *
 *  1. Workspace-uploaded logo (PhishTrainer Settings → branding) wins — the
 *     customer's own company logo when they've uploaded one. Since 2026-09-17
 *     there are TWO slots, `logoPath` (light) and `logoPathDark`, and either
 *     alone fills both themes — see resolvePlayerLogos for that fallback rule.
 *  2. Otherwise the HumanFirewall default wordmark, theme-aware via the `.dark`
 *     class the player ThemeContext toggles: dark theme → white logo,
 *     light theme → dark logo (no CSS-invert hacks; real assets per mode).
 *
 * Theme selection is done with CSS (`dark:hidden` / `hidden dark:block`) rather
 * than by reading the theme in JS, for both the tenant logo and the default —
 * a JS read runs after hydration and flashes the wrong asset first.
 */
export function PlayerBrandLogo() {
  const { t } = useTranslation()
  const [uploaded, setUploaded] = useState<PlayerLogos | null>(null)

  useEffect(() => {
    let cancelled = false
    getPlaySettings()
      .then((j) => {
        if (cancelled) return
        const branding = j?.data?.branding as
          | { logoPath?: string | null; logoPathDark?: string | null }
          | undefined
        const paths = resolvePlayerLogos({
          logoPath: branding?.logoPath,
          logoPathDark: branding?.logoPathDark,
        })
        if (!paths.light && !paths.dark) return

        // RESOLVE AGAINST THE API ORIGIN, do not merely normalise the slash.
        //
        // StorageService returns a ROOT-RELATIVE `/uploads/...` path and the API
        // serves it. The play portal runs on its own origin (middleware rewrites
        // by host), so a root-relative path resolves against the PLAY host and
        // 404s. The previous test here was `/^(https?:)?\//` — which a
        // `/uploads/...` value matches — so it was classified as "already
        // absolute" and used raw: the logo saved fine, previewed fine in
        // Settings, and was broken in the one place it was for.
        // resolveBackendFileUrl prefixes /uploads paths with the API base and
        // leaves a customer's own https CDN URL alone.
        const absolutize = (p: string | null): string | null => {
          if (!p) return null
          const raw = /^(https?:)?\//.test(p) ? p : `/${p}`
          return resolveBackendFileUrl(raw) ?? raw
        }

        setUploaded({
          light: absolutize(paths.light),
          dark: absolutize(paths.dark),
        })
      })
      .catch(() => {
        // Keep the default logo on error.
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (uploaded?.light || uploaded?.dark) {
    // Customer-uploaded logo. Both variants render; CSS picks one. When the
    // tenant uploaded only one, resolvePlayerLogos has already put the same
    // path in both slots, so this is identical to the old single-asset
    // behaviour for every tenant that has not filled the new slot.
    return (
      <>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={uploaded.light ?? undefined}
          alt={t('play.brand.logoAlt')}
          className={`block dark:hidden ${PLAYER_LOGO_MAX_HEIGHT_CLASS}`}
        />
        {/* The theme twin is DECORATIVE: exactly one of the pair is ever
            visible, and two images carrying the same alt text make a screen
            reader announce the brand twice. The wrapping <Link> in
            app/play/layout.tsx already has aria-label, so the control stays
            labelled on either theme. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={uploaded.dark ?? undefined}
          alt=""
          aria-hidden="true"
          className={`hidden dark:block ${PLAYER_LOGO_MAX_HEIGHT_CLASS}`}
        />
      </>
    )
  }

  // HumanFirewall default — swap the real asset by theme (CSS, no JS flash).
  // Sized by the same constant as a tenant logo: the reported "very small"
  // header (QA 2026-09-17) was this wordmark at h-7 / 28px.
  return (
    <>
      <Image
        src="/brand/humanfirewall-logo-light.svg"
        alt="HumanFirewall"
        width={176}
        height={28}
        priority
        className={`block dark:hidden ${PLAYER_LOGO_MAX_HEIGHT_CLASS}`}
      />
      {/* Decorative theme twin — see the note on the uploaded pair above. */}
      <Image
        src="/brand/humanfirewall-logo.svg"
        alt=""
        aria-hidden="true"
        width={176}
        height={28}
        priority
        className={`hidden dark:block ${PLAYER_LOGO_MAX_HEIGHT_CLASS}`}
      />
    </>
  )
}
